import { watch } from 'vue'
import { TabInitStatus, useInitStore } from '@/ContextTab/stores/init'
import { AuthStatus, useAuthStore } from '@/ContextTab/stores/auth/auth'
import { useCodesStore } from '@/ContextTab/stores/codes'
import { useGroupsAndUsersStore } from '@/ContextTab/stores/groupsAndUsers'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile'
import { tabMetrics } from '@/ContextTab/services/tabMetrics'
import { useAccess } from '@/ContextTab/stores/auth/useAccess'
import { useWaitingNewsStore } from '@/ContextTab/stores/newsSet/task-panel/waiting'
import { useMyNewsStore } from '@/ContextTab/stores/newsSet/task-panel/my'
import { useInProgressNewsStore } from '@/ContextTab/stores/newsSet/task-panel/inProgress'
import { useGatewayStore } from '@/ContextTab/stores/gateway'
import { useNewsStore } from '@/ContextTab/stores/news'
import { usePublishedNewsStore } from '@/ContextTab/stores/newsSet/published/published'
import { useProfileCodesStore } from '@/ContextTab/stores/profileCodes'
import type { useSet } from '@/ContextTab/stores/newsSet/useSet'


export function useAuthWatcher() {

  const userProfileStore = useUserProfileStore()
  const codesStore = useCodesStore()
  const profileCodesStore = useProfileCodesStore()
  const groupsAndUsersStore = useGroupsAndUsersStore()
  const authStore = useAuthStore()
  const initStore = useInitStore()
  const gatewayStore = useGatewayStore()
  const newsStore = useNewsStore()
  const publishedStore = usePublishedNewsStore()
  const myNewsStore = useMyNewsStore()
  const waitingNewsStore = useWaitingNewsStore()
  const inProgressNewsStore = useInProgressNewsStore()
  const { allowedTaskPanels } = useAccess()

  async function loadReferences(): Promise<boolean> {
    // both RequestResult and FennchInstance.get return types are defined
    // as Promise<any>, in the end...
    async function processRequests(requests: Promise<any>[]): Promise<boolean> {
      try {
        const responses = await Promise.all(requests)
        const hasError = responses.reduce(
          (hasError, response) => hasError || response.error,
          false,
        )
        return !hasError
      } catch (e) {
        tabMetrics.sendException(new Error('Error while loading references', { cause: e }))
        return false
      }
    }

    let success = await processRequests([
      // have to load profile before the rest
      userProfileStore.fetchData(),
    ])

    if (success) {
      success = await processRequests([
        codesStore.fetchCodes(),
        groupsAndUsersStore.fetchData(),
        profileCodesStore.fetchCodeProfile(1),
        profileCodesStore.fetchCodeProfile(2),
      ])
    }

    if (!success) {
      authStore.logout()
      authStore.setError('Не удалось подгрузить необходимые справочники, повторите попытку')
    }

    return success
  }

  function loadTaskPanel() {
    // profile.access must be loaded by now:
    allowedTaskPanels.value.forEach((panel) => {
      let store: Pick<ReturnType<typeof useSet>, 'loadNext'> | undefined

      if (panel === 'waiting') {
        store = useWaitingNewsStore()
      } else if (panel === 'my') {
        store = useMyNewsStore()
      } else if (panel === 'inProgress') {
        store = useInProgressNewsStore()
      }

      store?.loadNext()
    })
  }

  watch(() => authStore.status, async (authStatus) => {
    switch (authStatus) {
      case AuthStatus.authorized: {
        initStore.setState(TabInitStatus.loadingReferences)

        tabMetrics.setUser({
          id: authStore.userId ?? '',
          email: authStore.login ?? '',
          contextId: window.contextId,
        })

        const loaded = await loadReferences()
        loadTaskPanel()

        initStore.setState(loaded ? TabInitStatus.ready : TabInitStatus.notAuthorized)
        // continue initializetion in hooks/gatewayWatcher :: watch initStore.status
        break
      }

      case AuthStatus.notAuthorized: {
        gatewayStore.onLogout()
        tabMetrics.clearUser()
        initStore.setState(TabInitStatus.notAuthorized)
        ;[newsStore, publishedStore, myNewsStore, waitingNewsStore, inProgressNewsStore]
          .forEach((store) => store.clearStore())
        break
      }

      case AuthStatus.initial: {
        // impossible, (as we must not return to 'AuthStatus.init'), but for correctness:
        initStore.setState(TabInitStatus.waitingAuthCheck)
        break
      }
    }
  })

}
